import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  IMAGE_EDITOR_FORMATS,
  normalizeImageEditorFormat,
  physicalSizeToPixels,
} from '../../lib/imageEditorFormats';
import { buildSaveFilePreview } from '../../lib/imageEditorGuides';
import {
  clampImageEditorCanvasDimension,
  IMAGE_EDITOR_MAX_CANVAS_DIMENSION,
} from './ImageEditorNewCanvasDialog';

/**
 * Hub "New" dialog — size (px or cm/in + DPI), aspect lock, filename, format,
 * destination (project or Drafts). No CMYK / color-mode control.
 *
 * @param {{
 *   open: boolean,
 *   projects: Array<{ id: string, title?: string }>,
 *   projectsLoading?: boolean,
 *   defaultProjectId?: string,
 *   busy?: boolean,
 *   onCancel: () => void,
 *   onCreate: (doc: {
 *     width: number,
 *     height: number,
 *     filename: string,
 *     format: string,
 *     destinationProjectId: string|null,
 *   }) => void,
 * }} props
 */
export default function ImageEditorNewDocumentDialog({
  open,
  projects = [],
  projectsLoading = false,
  defaultProjectId = '',
  busy = false,
  onCancel,
  onCreate,
}) {
  const [unit, setUnit] = useState(/** @type {'px'|'cm'|'in'} */ ('px'));
  const [widthInput, setWidthInput] = useState('512');
  const [heightInput, setHeightInput] = useState('512');
  const [dpi, setDpi] = useState('72');
  const [lockAspect, setLockAspect] = useState(true);
  const [aspect, setAspect] = useState(1);
  const [filename, setFilename] = useState('untitled');
  const [format, setFormat] = useState('png');
  /** '' = Drafts */
  const [destinationProjectId, setDestinationProjectId] = useState('');

  useEffect(() => {
    if (!open) return;
    setUnit('px');
    setWidthInput('512');
    setHeightInput('512');
    setDpi('72');
    setLockAspect(true);
    setAspect(1);
    setFilename('untitled');
    setFormat('png');
    // Drafts by default when no project chosen / none available.
    setDestinationProjectId(
      typeof defaultProjectId === 'string' && defaultProjectId.trim()
        ? ''
        : '',
    );
  }, [open, defaultProjectId]);

  const pixelSize = useMemo(() => {
    const w = physicalSizeToPixels(Number(widthInput), unit, Number(dpi));
    const h = physicalSizeToPixels(Number(heightInput), unit, Number(dpi));
    return {
      width: clampImageEditorCanvasDimension(w),
      height: clampImageEditorCanvasDimension(h),
    };
  }, [widthInput, heightInput, unit, dpi]);

  const filenamePreview = useMemo(
    () => buildSaveFilePreview(filename, format),
    [filename, format],
  );

  const updateWidth = (raw) => {
    setWidthInput(raw);
    if (!lockAspect) return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0 || !aspect) return;
    setHeightInput(String(Math.max(1, Math.round(n / aspect))));
  };

  const updateHeight = (raw) => {
    setHeightInput(raw);
    if (!lockAspect) return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0 || !aspect) return;
    setWidthInput(String(Math.max(1, Math.round(n * aspect))));
  };

  const handleCreate = () => {
    if (!filenamePreview.valid) return;
    onCreate({
      width: pixelSize.width,
      height: pixelSize.height,
      filename: filenamePreview.fileStem,
      format: normalizeImageEditorFormat(format),
      destinationProjectId: destinationProjectId || null,
    });
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>New image</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={unit}
            onChange={(_e, next) => {
              if (!next) return;
              setUnit(next);
            }}
            aria-label="Size units"
          >
            <ToggleButton value="px">Pixels</ToggleButton>
            <ToggleButton value="cm">cm</ToggleButton>
            <ToggleButton value="in">inches</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1}>
            <TextField
              autoFocus
              size="small"
              label={`Width (${unit})`}
              type="number"
              value={widthInput}
              onChange={(e) => updateWidth(e.target.value)}
              inputProps={{ min: 0.01, step: unit === 'px' ? 1 : 0.01 }}
              fullWidth
              disabled={busy}
            />
            <TextField
              size="small"
              label={`Height (${unit})`}
              type="number"
              value={heightInput}
              onChange={(e) => updateHeight(e.target.value)}
              inputProps={{ min: 0.01, step: unit === 'px' ? 1 : 0.01 }}
              fullWidth
              disabled={busy}
            />
          </Stack>

          {unit !== 'px' ? (
            <TextField
              size="small"
              label="DPI"
              type="number"
              value={dpi}
              onChange={(e) => setDpi(e.target.value)}
              inputProps={{ min: 1, max: 600 }}
              sx={{ maxWidth: 160 }}
              disabled={busy}
              helperText="Converted to pixels under the hood"
            />
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={lockAspect}
                onChange={(e) => {
                  const next = e.target.checked;
                  setLockAspect(next);
                  if (next) {
                    const w = Number(widthInput) || 1;
                    const h = Number(heightInput) || 1;
                    setAspect(w / h);
                  }
                }}
                disabled={busy}
              />
            }
            label="Lock aspect ratio"
          />

          <Typography variant="caption" color="text.secondary">
            Canvas: {pixelSize.width} × {pixelSize.height} px (max{' '}
            {IMAGE_EDITOR_MAX_CANVAS_DIMENSION}px per side)
          </Typography>

          <TextField
            size="small"
            label="Filename"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            disabled={busy}
            fullWidth
            placeholder="e.g. hero_banner"
            helperText="Whole name — no forced prefix."
            inputProps={{ 'aria-label': 'Filename' }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            {filenamePreview.valid
              ? `Will create: ${filenamePreview.fileName}`
              : 'Enter a filename'}
          </Typography>

          <FormControl size="small" fullWidth disabled={busy}>
            <InputLabel id="new-doc-format-label">Format</InputLabel>
            <Select
              labelId="new-doc-format-label"
              label="Format"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              {IMAGE_EDITOR_FORMATS.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.label} — {f.hint}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth disabled={busy || projectsLoading}>
            <InputLabel id="new-doc-dest-label">Destination</InputLabel>
            <Select
              labelId="new-doc-dest-label"
              label="Destination"
              value={destinationProjectId}
              onChange={(e) => setDestinationProjectId(e.target.value)}
            >
              <MenuItem value="">
                <em>Drafts</em> (default)
              </MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {typeof p.title === 'string' && p.title.trim()
                    ? p.title.trim()
                    : p.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Drafts save to Company Files → Library → Image Editor. Project
            destination saves into that project’s gallery.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={busy || !filenamePreview.valid}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
